import { Fragment, type JSX } from 'react';
import {
  BrowserRouter, Navigate, Route, Routes as RouterRoutes,
} from 'react-router-dom';
import loadable from '@loadable/component';
import { PrivateRoute } from '@/components/PrivateRoute';
import { curationAtlasTabs, curationTabs } from '@/constants/Curation/tabs';
import { AnalysisSetProvider } from '@/contexts/AnalysisSetContext';
import { ClinicalProvider } from '@/contexts/ClinicalContext';
import { CurationContextProvider } from '@/contexts/CurationContext';
import { PatientProvider } from '@/contexts/PatientContext';
import { CurrentReportProvider } from '@/contexts/Reports/CurrentReportContext';
import DashboardLayout from '@/layouts/Dashboard';
import { LoadingPage } from '@/pages/Loading/Loading';
import PatientDashboardPage from '@/pages/Dashboard/PatientDashboardPage';
import TimelineTabContent from '@/components/PatientDashboard/TimelineTab/TimelineTabContent';
import LegacyCurationRouteRedirect from '@/pages/Curation/LegacyCurationRouteRedirect';

/* eslint-disable @typescript-eslint/naming-convention */
const LoginPage = loadable(() => import('@/pages/Login/Login'), {
  fallback: <LoadingPage />,
});
const LoginRedirectPage = loadable(() => import('@/pages/Login/Redirect'), {
  fallback: <LoadingPage />,
});
const ReportsPage = loadable(() => import('@/pages/Reports/ReportsPage'), {
  fallback: <LoadingPage />,
});
const Dashboard = loadable(() => import('@/pages/Dashboard/Dashboard'), {
  fallback: <LoadingPage />,
});
const PanelListView = loadable(() => import('@/pages/Atlas/PanelListView'), {
  fallback: <LoadingPage />,
});
const CurationTabContent = loadable(() => import('@/components/PatientDashboard/CurationTab/CurationTabContent'), {
  fallback: <LoadingPage />,
});
const MTBLayout = loadable(() => import('@/layouts/MTB'), {
  fallback: <LoadingPage />,
});
const CurationAtlasRoutes = loadable(() => import('@/pages/Atlas/CurationAtlasPage'), {
  fallback: <LoadingPage />,
});

export function Routes(): JSX.Element {
  return (
    <BrowserRouter>
      <RouterRoutes>
        <Route path="login">
          <Route index element={<LoginPage />} />
          <Route path="redirect" element={<LoginRedirectPage />} />
        </Route>
        <Route element={<PrivateRoute />}>
          <Route element={<DashboardLayout />}>
            {['/', '/curation', '/clinical', '/dashboard', '/meeting', '/files', '/reports', '/overview', 'central-review'].map((path) => (
              <Route
                key={path}
                index={path === '/'}
                path={path !== '/' ? path : undefined} // index takes care of the case where path = '/'
                element={<Dashboard />}
              />
            ))}
            <Route
              path="/atlas"
              element={<CurationAtlasRoutes />}
            >
              <Route
                index
                element={<Navigate to="gene-dashboard" replace />}
              />
              {curationAtlasTabs.map(({ to, children }) => (
                <Route
                  key={to}
                  path={to}
                  element={children}
                />
              ))}
              <Route
                key="gene-dashboard/:type/:idName"
                path="gene-dashboard/:type/:idName"
                element={(<PanelListView />)}
              />
            </Route>
          </Route>
          <Route path=":publicSubjectId" element={<PatientProvider />}>
            <Route path=":patientId">
              <Route
                index
                element={<Navigate to="timeline" replace />}
              />
              <Route
                path=":analysisSetId/curation/wisp"
                element={<Navigate to="../timeline" relative="route" replace />}
              />
              <Route element={<PatientDashboardPage scope="biomaterial.read" />}>
                <Route
                  path="timeline"
                  element={<TimelineTabContent />}
                />
              </Route>
              <Route element={<PatientDashboardPage scope="curation.sample.read" />}>
                <Route path=":analysisSetId" element={<AnalysisSetProvider />}>
                  <Route element={<CurationContextProvider />}>
                    <Route element={<CurrentReportProvider />}>
                      <Route path="curation" element={<CurationTabContent />}>
                        <Route index element={<Navigate to="profile" replace />} />
                        {curationTabs.map(({ to, children, altPaths = [] }) => (
                          <Fragment key={`curation-route-${to}`}>
                            <Route path={to} element={children} />
                            {altPaths.map((p) => (
                              <Route
                                key={p}
                                index={p.length === 0}
                                path={p.length ? p : undefined}
                                element={children}
                              />
                            ))}
                          </Fragment>
                        ))}
                      </Route>
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
          <Route path=":patientId" element={<PatientProvider />}>
            <Route path=":analysisSetId" element={<AnalysisSetProvider />}>
              <Route element={<CurationContextProvider />}>
                <Route element={<CurrentReportProvider />}>
                  <Route
                    path="reports"
                    element={<ReportsPage />}
                  >
                    <Route index />
                    <Route path=":reportId" />
                  </Route>
                </Route>
              </Route>
              <Route element={<CurrentReportProvider initialReportType="MTB_REPORT" />}>
                <Route
                  path="clinical/:clinicalVersionId/mtb/:view?"
                  element={(
                    <ClinicalProvider>
                      <MTBLayout />
                    </ClinicalProvider>
                  )}
                />
              </Route>
              <Route path="curation/:tab?" element={<LegacyCurationRouteRedirect />} />
            </Route>
          </Route>
        </Route>
      </RouterRoutes>
    </BrowserRouter>
  );
}
